import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminRateTables() {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newTable, setNewTable] = useState({ name: '', is_default: false });
  const [editingItem, setEditingItem] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const queryClient = useQueryClient();

  const { data: rateTables, isLoading: rateTablesLoading, error: rateTablesError } = useQuery({
    queryKey: ['rate-tables'],
    queryFn: async () => {
      console.log('🔍 Fetching rate tables...');
      const { data, error } = await supabase.rpc('admin_list_rate_tables');
      console.log('📊 Rate tables response:', { data, error });
      if (error) {
        console.error('❌ Rate tables error:', error);
        throw error;
      }
      // Transform the JSONB rate_items back to an array
      const transformed = data?.map((table: any) => ({
        ...table,
        rate_table_items: table.rate_items || []
      }));
      console.log('✅ Transformed rate tables:', transformed);
      return transformed;
    }
  });

  const { data: referralTypes, isLoading: referralTypesLoading, error: referralTypesError } = useQuery({
    queryKey: ['referral-types'],
    queryFn: async () => {
      console.log('🔍 Fetching referral types...');
      const { data, error } = await supabase.rpc('admin_list_referral_types');
      console.log('📊 Referral types response:', { data, error });
      if (error) {
        console.error('❌ Referral types error:', error);
        throw error;
      }
      return data;
    }
  });

  // Log errors to console
  if (rateTablesError) {
    console.error('Rate tables query error:', rateTablesError);
    toast.error('Failed to load rate tables: ' + (rateTablesError as any).message);
  }
  if (referralTypesError) {
    console.error('Referral types query error:', referralTypesError);
  }

  const createTableMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('admin_create_rate_table', {
        p_name: newTable.name,
        p_is_default: newTable.is_default
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Rate table created!');
      setIsAddModalOpen(false);
      setNewTable({ name: '', is_default: false });
      queryClient.invalidateQueries({ queryKey: ['rate-tables'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create rate table');
    }
  });

  const addItemMutation = useMutation({
    mutationFn: async (item: any) => {
      const { data, error } = await supabase.rpc('admin_create_rate_item', {
        p_rate_table_id: item.rate_table_id,
        p_referral_type_id: item.referral_type_id,
        p_commission_type: item.commission_type,
        p_commission_value: item.commission_value,
        p_minimum_threshold: item.minimum_threshold
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Rate item added!');
      setEditingItem(null);
      setIsEditMode(false);
      queryClient.invalidateQueries({ queryKey: ['rate-tables'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add rate item');
    }
  });

  const updateItemMutation = useMutation({
    mutationFn: async (item: any) => {
      const { error } = await supabase.rpc('admin_update_rate_item', {
        p_rate_item_id: item.id,
        p_commission_type: item.commission_type,
        p_commission_value: item.commission_value,
        p_minimum_threshold: item.minimum_threshold
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Rate item updated!');
      setEditingItem(null);
      setIsEditMode(false);
      queryClient.invalidateQueries({ queryKey: ['rate-tables'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update rate item');
    }
  });

  return (
    <div className="container mx-auto py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Rate Tables</h1>
        <Button onClick={() => setIsAddModalOpen(true)}>
          <Plus className="w-4 h-4 mr-2" /> Create Rate Table
        </Button>
      </div>

      {rateTablesLoading ? (
        <Card className="p-6">
          <p>Loading rate tables...</p>
        </Card>
      ) : rateTablesError ? (
        <Card className="p-6">
          <p className="text-destructive">Error loading rate tables: {(rateTablesError as any)?.message}</p>
        </Card>
      ) : !rateTables || rateTables.length === 0 ? (
        <Card className="p-6">
          <p className="text-muted-foreground">No rate tables found. Create your first one!</p>
        </Card>
      ) : null}

      <div className="grid gap-6">
        {rateTables?.map((table) => (
          <Card key={table.id} className="p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-semibold">{table.name}</h3>
                {table.is_default && (
                  <span className="text-sm text-muted-foreground">Default Rate Table</span>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium text-sm text-muted-foreground mb-2">Rate Items:</h4>
              {table.rate_table_items?.length === 0 ? (
                <p className="text-sm text-muted-foreground">No rate items configured</p>
              ) : (
                <div className="space-y-2">
                  {table.rate_table_items?.map((item: any) => (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div>
                        <p className="font-medium">{item.referral_types?.display_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {item.commission_type === 'flat' ? '$' : ''}{item.commission_value}
                          {item.commission_type === 'percentage' ? '%' : ''}
                          {item.minimum_threshold && ` (every ${item.minimum_threshold})`}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setIsEditMode(true);
                          setEditingItem({
                            id: item.id,
                            rate_table_id: table.id,
                            referral_type_id: item.referral_types?.id,
                            referral_type_name: item.referral_types?.display_name,
                            commission_type: item.commission_type,
                            commission_value: item.commission_value,
                            minimum_threshold: item.minimum_threshold
                          });
                        }}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setIsEditMode(false);
                  setEditingItem({ rate_table_id: table.id });
                }}
              >
                <Plus className="w-4 h-4 mr-2" /> Add Rate Item
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Create Rate Table Dialog */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Rate Table</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Table Name</Label>
              <Input
                placeholder="e.g., Standard Rates, Premium Rates"
                value={newTable.name}
                onChange={(e) => setNewTable({ ...newTable, name: e.target.value })}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_default"
                checked={newTable.is_default}
                onChange={(e) => setNewTable({ ...newTable, is_default: e.target.checked })}
              />
              <Label htmlFor="is_default">Set as default rate table</Label>
            </div>
            <Button onClick={() => createTableMutation.mutate()} className="w-full">
              Create Table
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Rate Item Dialog */}
      <Dialog open={!!editingItem} onOpenChange={() => {
        setEditingItem(null);
        setIsEditMode(false);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isEditMode ? 'Edit' : 'Add'} Rate Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!isEditMode && (
              <div>
                <Label>Referral Type</Label>
                <Select
                  value={editingItem?.referral_type_id}
                  onValueChange={(v) => setEditingItem({ ...editingItem, referral_type_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {referralTypes
                      ?.filter((type) => {
                        // Filter out referral types that already have rate items in this table
                        const currentTable = rateTables?.find(t => t.id === editingItem?.rate_table_id);
                        const existingTypeIds = currentTable?.rate_table_items?.map((item: any) => item.referral_types?.id || item.referral_type_id) || [];
                        return !existingTypeIds.includes(type.id);
                      })
                      .map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.display_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {referralTypes?.filter((type) => {
                  const currentTable = rateTables?.find(t => t.id === editingItem?.rate_table_id);
                  const existingTypeIds = currentTable?.rate_table_items?.map((item: any) => item.referral_types?.id || item.referral_type_id) || [];
                  return !existingTypeIds.includes(type.id);
                }).length === 0 && (
                  <p className="text-sm text-muted-foreground mt-1">All referral types already have rate items in this table</p>
                )}
              </div>
            )}
            {isEditMode && (
              <div>
                <Label>Referral Type</Label>
                <Input value={editingItem?.referral_type_name} disabled className="bg-muted" />
              </div>
            )}
            <div>
              <Label>Commission Type</Label>
              <Select
                value={editingItem?.commission_type}
                onValueChange={(v) => setEditingItem({ ...editingItem, commission_type: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="flat">Flat Amount ($)</SelectItem>
                  <SelectItem value="percentage">Percentage (%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Commission Value</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="e.g., 10 or 5.5"
                value={editingItem?.commission_value || ''}
                onChange={(e) => setEditingItem({ ...editingItem, commission_value: e.target.value })}
              />
            </div>
            <div>
              <Label>Minimum Threshold (optional)</Label>
              <Input
                type="number"
                placeholder="e.g., 25 for 'every 25 referrals'"
                value={editingItem?.minimum_threshold || ''}
                onChange={(e) => setEditingItem({ ...editingItem, minimum_threshold: e.target.value })}
              />
            </div>
            <Button 
              onClick={() => {
                const itemData = {
                  ...editingItem,
                  commission_value: parseFloat(editingItem?.commission_value || '0'),
                  minimum_threshold: editingItem?.minimum_threshold ? parseInt(editingItem.minimum_threshold) : null
                };
                
                if (isEditMode) {
                  updateItemMutation.mutate(itemData);
                } else {
                  addItemMutation.mutate({ ...itemData, is_active: true });
                }
              }} 
              className="w-full"
            >
              {isEditMode ? 'Update' : 'Add'} Rate Item
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
